import { Box, Tooltip, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { Fragment } from "react";
import type { FurnaceBillet } from "../models/CampaignData";

interface FurnaceBilletsBoxProps {
	billets: FurnaceBillet[];
	/** Same row template as the header, so each billet lines up with its header line. */
	rows: string;
}

const POSITIONS = [
	{
		position: "Next",
		label: "Next",
		hint: "Next billet to be discharged from the reheating furnace",
		tone: (t: Theme) => t.palette.primary.main,
	},
	{
		position: "Current",
		label: "Current",
		hint: "Last billet discharged from the reheating furnace",
		tone: (t: Theme) => t.palette.success.main,
	},
] as const;

// Prometheus sends the order as "<work order>-<billet number within it>"
const workOrderOf = (b?: FurnaceBillet) => b?.OrderId?.split("-")[0] ?? null;

const sizeOf = (b?: FurnaceBillet) => {
	if (!b?.BilletSize) return null;
	if (b.Length == null) return b.BilletSize;
	return `${b.BilletSize} · ${Number.parseFloat(b.Length.toFixed(1))} in`;
};

const FIELDS = [
	{
		label: "Billet",
		value: (b?: FurnaceBillet) => b?.BilletId?.toString() ?? null,
		compare: false,
	},
	{ label: "WO", value: workOrderOf, compare: true },
	{
		label: "Heat",
		value: (b?: FurnaceBillet) => b?.HeatId ?? null,
		compare: true,
	},
	{
		label: "Grade",
		value: (b?: FurnaceBillet) => b?.GradeId ?? null,
		compare: true,
	},
	{ label: "Size", value: sizeOf, compare: true },
];

const labelSx = { fontSize: 13, color: "#888", textAlign: "right" as const };
const valueSx = {
	fontSize: 13,
	fontWeight: "bold",
	whiteSpace: "nowrap" as const,
};

export const FurnaceBilletsBox = ({
	billets,
	rows,
}: FurnaceBilletsBoxProps) => {
	const current = billets.find((b) => b.Position === "Current");

	return (
		<Box
			data-testid="furnace-billets"
			sx={{
				display: "grid",
				// pill, then label + value per field, with a thin rule column between fields
				gridTemplateColumns: `auto ${FIELDS.map((_, i) => (i < FIELDS.length - 1 ? "auto auto auto" : "auto auto")).join(" ")}`,
				gridTemplateRows: rows,
				columnGap: 0.75,
				alignItems: "center",
			}}
		>
			{POSITIONS.map((pos, row) => {
				const billet = billets.find((b) => b.Position === pos.position);
				return (
					<Fragment key={pos.position}>
						<Tooltip title={pos.hint}>
							<Box
								component="span"
								sx={(t) => ({
									// Both pills take the column width, so Next and Current match
									justifySelf: "stretch",
									textAlign: "center",
									mr: 1,
									px: 1,
									py: 0.125,
									borderRadius: 999,
									fontSize: 12,
									fontWeight: 600,
									lineHeight: 1.4,
									color: pos.tone(t),
									bgcolor: alpha(pos.tone(t), 0.12),
								})}
							>
								{pos.label}
							</Box>
						</Tooltip>
						{FIELDS.map((field, i) => {
							const value = field.value(billet);
							// The next billet's values that differ from the current one, so the change is seen coming
							const changes =
								pos.position === "Next" &&
								field.compare &&
								value != null &&
								current != null &&
								value !== field.value(current);
							return (
								<Fragment key={field.label}>
									<Typography sx={labelSx}>
										{row === 0 ? `${field.label}:` : ""}
									</Typography>
									<Tooltip
										title={changes ? "Changes from the current billet" : ""}
									>
										<Typography
											component="span"
											sx={(t) => ({
												...valueSx,
												mr: 0.75,
												justifySelf: "start",
												...(changes && {
													color: t.palette.primary.main,
													bgcolor: alpha(t.palette.primary.main, 0.12),
													borderRadius: 0.5,
													px: 0.5,
												}),
											})}
										>
											{billet ? value || "-" : "-"}
										</Typography>
									</Tooltip>
									{i < FIELDS.length - 1 && (
										<Box
											sx={{
												width: "1px",
												height: 14,
												mr: 0.75,
												bgcolor: "divider",
											}}
										/>
									)}
								</Fragment>
							);
						})}
					</Fragment>
				);
			})}
		</Box>
	);
};
